import clsx from 'clsx';
import { useFormik } from 'formik';
import { Link, useNavigate } from 'react-router-dom';

import { Alert, KeenIcon } from '@/components';
import { useForgotPasswordMutation } from '@/services/AuthService';
import { useSnackbar } from 'notistack';
import { emailValidation } from '@/utils/validations';
import { getErrorMessage } from '@/utils/errorUtils';
import * as Yup from 'yup';

const initialValues = {
  email: '',
};

const forgotPasswordSchema = Yup.object().shape({
  email: emailValidation(),
});

const ResetPassword = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [forgotPassword] = useForgotPasswordMutation();

  const formik = useFormik({
    initialValues,
    validationSchema: forgotPasswordSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      try {
        setStatus(undefined);

        const response = await forgotPassword({
          email: values.email,
          isAdminPanel: true,
        }).unwrap();

        localStorage.setItem('resetEmail', values.email);
        enqueueSnackbar(
          response.message ||
            'Password reset link sent. Please check your email to proceed.',
          {
            variant: 'success',
          }
        );
        navigate('/auth/reset-password/check-email', {
          state: { email: values.email },
        });
      } catch (error) {
        const errorMessage = getErrorMessage(
          error,
          'Unable to send password reset link. Please try again.'
        );

        setStatus(errorMessage);
        enqueueSnackbar(errorMessage, { variant: 'error' });
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <div className="card max-w-[390px] w-full contrast:!bg-card">
      <form
        aria-label="Request a password reset"
        className="card-body flex flex-col gap-5 p-10"
        noValidate
        onSubmit={formik.handleSubmit}
      >
        <div className="text-center">
          <h1 className="text-lg font-semibold text-gray-900 mb-2">
            Forgot password?
          </h1>
          <span className="text-2sm text-gray-600 font-medium">
            Enter your email and we&apos;ll send you a reset link.
          </span>
        </div>

        {formik.status && <Alert variant="danger">{formik.status}</Alert>}

        <div className="flex flex-col gap-1">
          <label
            htmlFor="email"
            className="form-label text-gray-900"
            aria-label="Email"
          >
            Email
          </label>
          <input
            id="email"
            type="email"
            placeholder="Enter email address"
            autoComplete="email"
            {...formik.getFieldProps('email')}
            onInput={(event) => {
              const input = event.target as HTMLInputElement;
              input.value = input.value.replace(/\s/g, '');
              formik.setFieldValue('email', input.value);
            }}
            className={clsx(
              'form-control input !text-gray-900 placeholder:!text-gray-800',
              {
                'is-invalid': formik.touched.email && formik.errors.email,
              }
            )}
          />
          {formik.touched.email && formik.errors.email && (
            <span
              role="alert"
              className="text-danger contrast:text-red-500 text-xs mt-1"
            >
              {formik.errors.email}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-5 items-stretch">
          <button
            type="submit"
            className="btn btn-primary flex justify-center grow"
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting ? 'Sending reset link...' : 'Continue'}
          </button>

          <Link
            to="/auth/login"
            className="flex items-center justify-center text-sm gap-2 text-gray-700 hover:text-primary"
          >
            <KeenIcon icon="black-left" />
            Back to Login
          </Link>
        </div>
      </form>
    </div>
  );
};

export { ResetPassword };
